<template>
  <div class="game">
    <VueUnity :unity="unityContext" :width="canvasWidth" :height="canvasHeight" />
    <div class="game-cover" v-if="!loaded">
      <div class="game-loader">
        <IconLogo />
        <el-progress :text-inside="true" :stroke-width="24" :percentage="progress" />
      </div>
    </div>
    <Transition name="fade-in">
      <div v-if="levelCompleted" class="game-cover">
        <LineMask />
        <div class="game-completed">
          <div class="user-passes">
            {{ userStore.user?.passes }}
            <IconPass />
          </div>
          <div class="cover-title">Level Cleared</div>
          <div class="cover-score">
            Earned {{ score }}
            <IconLP />
          </div>
          <template v-if="userStore.user!.passes > 0">
            <div class="hint-text">
              Each game worth 1
              <IconPass />
            </div>
            <LeeaBtn color="white" @click="start" class="button">Next Level</LeeaBtn>
          </template>
          <template v-else>
            <div class="hint-text">
              You have ran out of Play Passes. <br />
              Invite frineds to earn more.
            </div>
            <LeeaBtn color="white" @click="goToFriendsTab" class="button">Invite Friends</LeeaBtn>
          </template>
        </div>
      </div>
    </Transition>
    <Transition name="fade-in">
      <div v-if="levelFailed" class="game-cover">
        <LineMask />
        <div class="game-failed">
          <div class="user-passes">
            {{ userStore.user?.passes }}
            <IconPass />
          </div>
          <div class="cover-score">Level Failed :(</div>
          <template v-if="userStore.user!.passes > 0">
            <div class="hint-text">
              Each game worth 1
              <IconPass />
            </div>
            <LeeaBtn color="white" @click="restart" class="button">Restart</LeeaBtn>
          </template>
          <template v-else>
            <div class="hint-text">
              You have ran out of Play Passes. <br />
              Invite frineds to earn more.
            </div>
            <LeeaBtn color="white" @click="goToFriendsTab" class="button">Invite Friends</LeeaBtn>
          </template>
        </div>
      </div>
    </Transition>
    <Confetti class="confetti" ref="confetti"></Confetti>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { hapticService } from '@/services/haptic.service'
import { api } from '@/api'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import { analyticsService } from '@/services/analytics.service'
import UnityWebgl from 'unity-webgl'
import VueUnity from 'unity-webgl/vue'
import Confetti from '@/components/Confetti.vue'
import LineMask from '@/components/LineMask.vue'
import LeeaBtn from '@/components/LeeaBtn.vue'
import IconPass from '@/components/Icons/IconPass.vue'
import IconLP from '@/components/Icons/IconLP.vue'
import IconLogo from '@/components/Icons/IconLogo.vue'

const router = useRouter()
const userStore = useUserStore()

const unityContext = new UnityWebgl({
  loaderUrl: '/leea_sort/Build/leea_sort.loader.js',
  dataUrl: '/leea_sort/Build/leea_sort.data.gz',
  frameworkUrl: '/leea_sort/Build/leea_sort.framework.js.gz',
  codeUrl: '/leea_sort/Build/leea_sort.wasm.gz'
})

const canvasWidth = ref(window.innerWidth)
const canvasHeight = ref(window.innerHeight)
const loaded = ref(false)
const progress = ref(0)
const confetti = ref()

const score = ref(0)
const levelCompleted = ref(false)
const levelFailed = ref(false)

unityContext.on('progress', (p) => {
  progress.value = Math.round(p * 100)
})

const updateCanvasSize = () => {
  canvasWidth.value = window.innerWidth
  canvasHeight.value = window.innerHeight
}

const goToFriendsTab = () => {
  analyticsService.logNavigateToInvite()
  router.push('/app/friends')
}

const exit = () => {
  api.game.end()
  unityContext.unload()
  router.push('/app/tap')
}

const onGameLoaded = () => {
  loaded.value = true
  start()
}

const start = async () => {
  await api.game.end()
  const isAvailable = await api.game.start()
  if (!isAvailable) {
    ElMessage.warning('No Play Passes left :(')
    return exit()
  }

  userStore.fetchUser()

  loadNextLevel()
}

const onLevelComplete = async (e: Event) => {
  const event = e as CustomEvent
  confetti.value.explode()
  const level = event.detail.level
  const reward = await api.game.claim({
    payload: {
      level
    }
  })
  score.value = reward.points
  levelCompleted.value = true
  analyticsService.logGameCompleted()
}

const loadNextLevel = () => {
  levelCompleted.value = false
  levelFailed.value = false
  unityContext.send('Game', 'StartLevel')
  analyticsService.logGameStart()
}

const restart = async () => {
  await api.game.end()
  start()
}

const onLevelFailed = (e: Event) => {
  const event = e as CustomEvent
  const level = event.detail.level
  analyticsService.logGameFailed()
  exit()
}

const onPlayHaptic = (e: Event) => {
  hapticService.vibrate()
}

onMounted(() => {
  window.Telegram.WebApp.BackButton.show()
  window.Telegram.WebApp.onEvent('backButtonClicked', () => {
    return exit()
  })

  window.addEventListener('resize', updateCanvasSize)
  window.addEventListener('gameLoaded', onGameLoaded)
  window.addEventListener('levelComplete', onLevelComplete)
  window.addEventListener('levelFailed', onLevelFailed)
  window.addEventListener('playHaptic', onPlayHaptic)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateCanvasSize)
  window.removeEventListener('gameLoaded', onGameLoaded)
  window.removeEventListener('levelComplete', onLevelComplete)
  window.removeEventListener('levelFailed', onLevelFailed)
  window.removeEventListener('playHaptic', onPlayHaptic)
})
</script>

<style lang="scss">
@keyframes dash {
  0% {
    stroke-dashoffset: 4000;
  }

  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes wide {
  0% {
    stroke-width: 30px;
  }

  100% {
    stroke-width: 500px;
  }
}

html,
body,
#app {
  margin: 0;
  padding: 0;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
}

.cover-title {
  font-size: 36px;
  letter-spacing: 0.02em;
  font-family: 'Forma DJR Display';
  font-weight: 700;
  margin-bottom: 20px;
}

#unity-canvas-1 {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
}

.game-cover {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background-size: cover;

  .line {
    width: 100%;
    height: 100%;
    transform: scale(2.5);
    transform: translateX(-150%);
    stroke-dasharray: 4000;
    stroke-dashoffset: 4000;
    animation:
      dash 1s ease-in forwards,
      wide 0.5s ease-in 0.5s forwards;
    stroke-width: 30px;
    stroke: white;
    stroke-linecap: round;

    #line {
      path {
        transform: translateX(-200px);
      }
    }
  }
}

.game-completed,
.game-loader,
.game-failed {
  background: url('@/assets/bg-shapes.png'), linear-gradient(#74a3ff, #256fff);
  background-size: cover;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: end;
  flex-direction: column;
  padding: 60px 20px 80px 20px;
  color: white;
  mask: url('#line');

  .user-passes {
    font-family: 'Forma DJR Display';
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 10px;
    width: 100%;
    margin-bottom: 150px;

    .icon {
      fill: white;
      height: 20px;
      width: 20px;
    }
  }

  .hint-text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    margin-bottom: 10px;
    text-align: center;

    .icon {
      fill: white;
      width: 20px;
      height: 20px;
    }
  }

  .cover-score {
    font-size: 36px;
    letter-spacing: 0.02em;
    font-family: 'Forma DJR Display';
    font-weight: 700;
    margin-bottom: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;

    .icon {
      fill: white;
    }
  }
}

.game-loader {
  mask: none;

  .icon {
    fill: white;
  }

  .el-progress {
    width: 100%;
    align-items: end;
    margin-top: 60px;

    .el-progress-bar__outer {
      background-color: lightgrey;

      .el-progress-bar__inner {
        background-color: white;

        .el-progress-bar__innerText {
          color: var(--blue-color);
        }
      }
    }
  }
}

.button {
  background-color: black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  border-radius: 12px;
  font-size: 16px;
}

.confetti {
  position: fixed;
  top: 0;
  left: 0;
}
</style>
